import {useCallback, useMemo, useState} from 'react'

/**
 * Persists a piece of UI state to `localStorage` under the given key.
 *
 * Feature-local hook (mirrors `@features/operator-view`'s `useLocalStorageSettings`)
 * used to replace the legacy hcem `usePersistingSettingState` / `usePlannerUISettings`
 * (which relied on the hcem-only `LocalStorageSettingsProvider` context). This version
 * talks to `localStorage` directly, so it works inside Suite without any provider.
 * Storage keys are shared with the legacy app via `ProductionPlanningLSSettingsKeys`,
 * so persisted preferences carry over.
 */
export const useLocalStorageSettings = <T extends Record<string, unknown>>(
  key: string,
  initial: T
): {settings: T; updateSettings: (newSettings: T) => void} => {
  const memoizedInitial = useMemo(() => initial, []) // eslint-disable-line react-hooks/exhaustive-deps

  const getFromStorage = useCallback((): T | undefined => {
    try {
      const raw = localStorage.getItem(key)
      return raw ? (JSON.parse(raw) as T) : undefined
    } catch {
      return undefined
    }
  }, [key])

  const setToStorage = useCallback(
    (value: T) => {
      try {
        localStorage.setItem(key, JSON.stringify(value))
      } catch {
        // ignore storage errors (e.g. storage full or disabled)
      }
    },
    [key]
  )

  const [state, setState] = useState<T>(() => getFromStorage() ?? memoizedInitial)

  const updateSettings = useCallback(
    (newSettings: T) => {
      setState(newSettings)
      setToStorage(newSettings)
    },
    [setToStorage]
  )

  return {settings: state, updateSettings}
}
